// 👇 Place this at the top of your file
import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase/config";
import {
  doc,
  setDoc,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  getDoc,
} from "firebase/firestore";
import DeliverySlotSelector from "../components/DeliverySlotSelector";
import ConfirmSubscription from "../components/ConfirmSubscription";
import { RAZORPAY_KEY_ID } from "../config/razorpay";
import { isWithinDeliveryRange } from "../utils/getDistanceInKm";
import { createWelcomeCoupon, createReferralCoupon, markReferralCompleted } from "../utils/referralUtils";
import { getSubscriptionStatus } from "../utils/subscriptionStatusUtils";

const GOOGLE_MAPS_API_KEY = "AIzaSyBnWSiMg6PDeqo8Rij0i8_YpMBqGXIl7QQ";

const SubscribePage = ({ plan, onComplete }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [slot, setSlot] = useState("Morning");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [addressDetails, setAddressDetails] = useState("");
  const [location, setLocation] = useState("");
  const [area, setArea] = useState("");
  const [lat, setLat] = useState(null);
  const [lng, setLng] = useState(null);

  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [couponMessage, setCouponMessage] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [outOfRange, setOutOfRange] = useState(false);
  const [giftMode, setGiftMode] = useState(false);
  const [recipientName, setRecipientName] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [recipientAddress, setRecipientAddress] = useState("");
  const [recipientArea, setRecipientArea] = useState("");
  const [recipientLat, setRecipientLat] = useState(null);
  const [recipientLng, setRecipientLng] = useState(null);

  const [areaList, setAreaList] = useState([]);
  const [errors, setErrors] = useState({});
  // New state for active subscription check
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [pastSubscriptions, setPastSubscriptions] = useState([]);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  // New state for dynamic plan configuration
  const [planData, setPlanData] = useState(null);
  const [rotiCount, setRotiCount] = useState(0);
  const [loadingPlan, setLoadingPlan] = useState(true);
  // Add timeout for loading states to prevent infinite loading
  const [loadingTimeout, setLoadingTimeout] = useState(false);

  // Validation function
  const validate = () => {
    const newErrors = {};
    if (!name) newErrors.name = 'Required';
    if (!phone) newErrors.phone = 'Required';
    if (!area) newErrors.area = 'Required';
    if (!addressDetails) newErrors.addressDetails = 'Required';
    if (!location) newErrors.location = 'Required';
    
    // Debug logging for area and userName
    console.log("🔍 Validation Debug - Area:", area);
    console.log("🔍 Validation Debug - Name:", name);
    console.log("🔍 Validation Debug - UserName (should be same as name):", name);
    
    return newErrors;
  };

  const startDate = new Date();
  startDate.setDate(startDate.getDate() + 1);
  const formattedDate = startDate.toISOString().split("T")[0];

  // Fetch plan data from Firestore
  useEffect(() => {
    console.log("🔍 Plan object:", plan); // Debug log
    if (plan?.id) {
      console.log("🔍 Fetching plan data for ID:", plan.id); // Debug log
      const fetchPlanData = async () => {
        try {
          setLoadingPlan(true);
          const docRef = doc(db, "subscription_plans", plan.id);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            console.log("🔍 Plan data fetched:", data); // Debug log
            setPlanData(data);
            // Set initial roti count to minimum (minimum 2 rotis)
            setRotiCount(Math.max(data.minRotisPerDay || 2, 2));
          } else {
            console.error("❌ Plan not found in Firestore for ID:", plan.id);
            // Set default plan data if not found (minimum 2 rotis)
            setPlanData({
              minRotisPerDay: 2,
              maxRotisPerDay: 10,
              pricePerRoti: 20,
              deliveryDays: 26
            });
            setRotiCount(2);
          }
        } catch (error) {
          console.error("❌ Error fetching plan data:", error);
          // Set default plan data on error (minimum 2 rotis)
          setPlanData({
            minRotisPerDay: 2,
            maxRotisPerDay: 10,
            pricePerRoti: 20,
            deliveryDays: 26
          });
          setRotiCount(2);
        } finally {
          setLoadingPlan(false);
        }
      };
      fetchPlanData();
    } else {
      console.log("❌ No plan ID found, using defaults"); // Debug log
      // Set default plan data if no plan ID (minimum 2 rotis)
      setPlanData({
        minRotisPerDay: 2,
        maxRotisPerDay: 10,
        pricePerRoti: 20,
        deliveryDays: 26
      });
      setRotiCount(2);
      setLoadingPlan(false);
    }
  }, [plan?.id]);

  // Add timeout for loading states to prevent infinite loading
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading || checkingSubscription || loadingPlan) {
        console.warn("⚠️ SubscribePage loading timeout - forcing render");
        setLoadingTimeout(true);
      }
    }, 8000); // 8 second timeout
    
    return () => clearTimeout(timeout);
  }, [loading, checkingSubscription, loadingPlan]);

  // Calculate dynamic total amount
  const totalAmount = useMemo(() => {
    if (!planData) return 0;
    return rotiCount * (planData.pricePerRoti || 20) * (planData.deliveryDays || 26);
  }, [rotiCount, planData]);

  // Use dynamic total amount instead of static plan price
  const planPrice = totalAmount;

  useEffect(() => {
    if (!plan) {
      // Redirect to home after 2 seconds
      const timer = setTimeout(() => navigate("/"), 2000);
      return () => clearTimeout(timer);
    }
  }, [plan, navigate]);

  // Fetch area list from Firestore
  useEffect(() => {
    async function fetchAreas() {
      try {
        const areasDoc = await getDoc(doc(db, "app_settings", "areas"));
        if (areasDoc.exists()) {
          setAreaList(areasDoc.data().list || []);
        }
      } catch (err) {
        console.error("Failed to fetch areas:", err);
      }
    }
    fetchAreas();
  }, []);

  // Top-level effect to check for active subscription
  useEffect(() => {
    if (!user?.uid) return;
    setCheckingSubscription(true);
    async function checkActiveAndPastSubscriptions() {
      try {
        const subsRef = collection(db, "subscriptions");
        const q = query(subsRef, where("userId", "==", user.uid));
        const snapshot = await getDocs(q);
        let foundActive = false;
        let past = [];
        // Check each subscription and update status if needed
        for (const docSnap of snapshot.docs) {
          const data = docSnap.data();
          const statusResult = await getSubscriptionStatus(docSnap.id);
          const updatedData = statusResult.subscription || data;
          
          if (updatedData.status === "active") {
            foundActive = true;
          }
          past.push({ id: docSnap.id, ...updatedData });
        }
        setHasActiveSubscription(foundActive);
        setPastSubscriptions(past);
      } catch (err) {
        setHasActiveSubscription(false);
        setPastSubscriptions([]);
      } finally {
        setCheckingSubscription(false);
      }
    }
    checkActiveAndPastSubscriptions();
  }, [user]);

  const handleLocateMe = () => {
    if (!navigator.geolocation) {
      alert("❌ Geolocation not supported.");
      return;
    }

    navigator.geolocation.getCurrentPosition(async (position) => {
      const lat = position.coords.latitude;
      const lng = position.coords.longitude;
      setLat(lat);
      setLng(lng);
      setLocation(`Lat: ${lat.toFixed(3)}, Lon: ${lng.toFixed(3)}`);

      // Check delivery range (temporarily disabled for testing)
      // if (!isWithinDeliveryRange(lat, lng)) {
      //   setOutOfRange(true);
      //   alert("Sorry, we do not deliver to your location yet.");
      // } else {
      //   setOutOfRange(false);
      // }
      setOutOfRange(false); // Temporarily allow all locations

      try {
        const res = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`
        );
        const data = await res.json();
        const components = data.results[0]?.address_components || [];
        const sublocality = components.find(c =>
          c.types.includes("sublocality") || c.types.includes("locality")
        );
        if (sublocality) {
          setArea(sublocality.long_name);
        }
      } catch (err) {
        console.error("Geocoding failed:", err);
      }
    });
  };

  const applyCoupon = async () => {
    if (!coupon.trim()) {
      setCouponMessage("❌ Please enter a coupon code.");
      return;
    }

    setApplyingCoupon(true);
    const code = coupon.trim().toUpperCase();

    try {
      const couponsRef = collection(db, "coupons");
      const q = query(couponsRef, where("code", "==", code), where("isActive", "==", true));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        setDiscount(0);
        setAppliedCoupon(null);
        setCouponMessage("❌ Invalid or inactive coupon code.");
        return;
      }

      const couponData = snapshot.docs[0].data();
      const couponId = snapshot.docs[0].id;

      if (new Date(couponData.expiryDate) < new Date()) {
        setCouponMessage("❌ This coupon has expired.");
        return;
      }

      if (couponData.usedBy && couponData.usedBy[user.uid]) {
        setAppliedCoupon(null);
        setCouponMessage('❌ You have already used this coupon.');
        setApplyingCoupon(false);
        return;
      }

      const discountPercent = Number(couponData.discountPercent || 0);
      const maxDiscount = Number(couponData.maxDiscountAmount || Infinity);
      const appliedDiscount = Math.min((planPrice * discountPercent) / 100, maxDiscount);

      setDiscount(appliedDiscount);
      setAppliedCoupon({ ...couponData, id: couponId });
      setCouponMessage(`🎉 ${discountPercent}% off applied! You saved ₹${appliedDiscount.toFixed(0)}`);
    } catch (error) {
      console.error("Coupon error:", error);
      setCouponMessage("❌ Error applying coupon.");
    } finally {
      setApplyingCoupon(false);
    }
  };

  const handlePayNow = async () => {
    if (outOfRange) {
      alert("Sorry, we do not deliver to your location yet.");
      return;
    }
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    const finalAmount = planPrice - discount;
    const response = await fetch("https://createrazorpayorder-swz7s7kyqq-uc.a.run.app", {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
        amount: finalAmount,
        currency: 'INR',
          receipt: `receipt_${Date.now()}`,
        planName: plan.name,
        }),
      });
    const data = await response.json();
    if (!data.success) {
      alert('Order creation failed');
      return;
    }
    const { order } = data;
    console.log("[DEBUG] Order from backend:", order);

    console.log("RAZORPAY_KEY_ID:", RAZORPAY_KEY_ID); // Debug log
    const options = {
      key: RAZORPAY_KEY_ID,
      amount: order.amount,
      currency: order.currency,
      name: 'JR Home Made Foods',
      description: plan.name,
      order_id: order.id,
      handler: async function (response) {
        await handlePaymentSuccess({
          paymentId: response.razorpay_payment_id,
          orderId: response.razorpay_order_id,
        });
      },
      prefill: {
        name,
        email: user?.email,
        contact: phone,
      },
      theme: { color: '#38a169' },
    };

    console.log("[DEBUG] Razorpay options:", options); // Debug log
    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', handlePaymentError);
    rzp.open();
  };

  const handleProceedToPayment = async () => {
    if (!user?.uid || !name || !phone || !location || !addressDetails || !area) {
      alert("⚠️ Please fill in all required fields.");
      return;
    }

    const subsRef = collection(db, "subscriptions");
    const existingQ = query(subsRef, where("userId", "==", user.uid), where("status", "==", "active"));
    const snapshot = await getDocs(existingQ);

    if (!snapshot.empty) {
      alert("❌ You already have an active subscription.");
      return;
    }

    setShowPayment(true);

    try {
      const subData = {
        userId: user.uid,
        name: name || "",
        userName: name || "", // For admin compatibility
        userEmail: user.email || "", // For admin compatibility
        email: user.email || "",
        planName: plan.name || "",
        price: planPrice || 0,
        discount: discount || 0,
        finalPrice: (planPrice || 0) - (discount || 0),
        rotisPerDay: plan.rotisPerDay || 0,
        deliverySlot: slot || "Morning",
        phone: phone || "",
        area: area || "",
        address: addressDetails || "",
        location: location || "",
        lat: lat || null,
        lng: lng || null,
        // Store clean address for maps
        addressForMaps: addressDetails || "",
        coordinates: lat && lng ? `${lat},${lng}` : null,
        startDate: formattedDate || "",
        status: "active",
        paymentStatus: "pending",
        paymentMethod: "cash_on_delivery",
        createdAt: serverTimestamp(),
        ...(appliedCoupon && {
          couponCode: appliedCoupon.code,
          couponId: appliedCoupon.id,
          couponDiscount: appliedCoupon.discountPercent,
        }),
      };

      // Debug logging for subscription data
      console.log("🔍 Subscription Data Debug (ProceedToPayment) - Area:", subData.area);
      console.log("🔍 Subscription Data Debug (ProceedToPayment) - UserName:", subData.userName);
      console.log("🔍 Subscription Data Debug (ProceedToPayment) - Name:", subData.name);

      await setDoc(doc(db, "subscriptions", user.uid), subData);
      
      // Handle referral coupon creation
      try {
        // Check if user was referred
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists() && userDoc.data().referredBy) {
          const referrerId = userDoc.data().referredBy;
          
          // Create welcome coupon for referred user
          const welcomeCouponId = await createWelcomeCoupon(user.uid, user.email);
          
          // Create referral coupon for referrer
          const referralCouponId = await createReferralCoupon(referrerId, user.uid, user.email);
          
          // Mark referral as completed
          const referralsRef = collection(db, "referrals");
          const referralQuery = query(
            referralsRef,
            where("referrerId", "==", referrerId),
            where("referredUserId", "==", user.uid)
          );
          const referralSnapshot = await getDocs(referralQuery);
          if (!referralSnapshot.empty) {
            const referralId = referralSnapshot.docs[0].id;
            await markReferralCompleted(referralId, referralCouponId, welcomeCouponId);
          }
          
          console.log("🎁 Referral coupons created successfully");
        }
      } catch (error) {
        console.error("❌ Error creating referral coupons:", error);
        // Don't block subscription if referral fails
      }
      
      alert("🎉 Subscription successful! Pay on delivery.");
      onComplete?.();
      navigate("/myplan");
    } catch (err) {
      console.error("❌ Error:", err);
      alert("Something went wrong. Try again.");
    }
  };

  // 👇 Cash on delivery (fallback)
  const placeOrder = async () => {
    if (!user?.uid || !name || !phone || !location || !addressDetails || !area) {
      return alert("⚠️ Please fill in all required fields.");
    }

    try {
      const subsRef = collection(db, "subscriptions");
      const existingQ = query(subsRef, where("userId", "==", user.uid), where("status", "==", "active"));
      const snapshot = await getDocs(existingQ);

      // Check if any subscription should be marked as completed
      let hasActiveSubscription = false;
      for (const docSnap of snapshot.docs) {
        const statusResult = await getSubscriptionStatus(docSnap.id);
        if (statusResult.subscription?.status === "active") {
          hasActiveSubscription = true;
          break;
        }
      }

      if (hasActiveSubscription) {
        alert("❌ You already have an active subscription.");
        return;
      }

      const subData = {
        userId: user.uid,
        name: name || "",
        userName: name || "", // For admin compatibility
        userEmail: user.email || "", // For admin compatibility
        email: user.email || "",
        planName: plan.name || "",
        price: planPrice || 0,
        discount: discount || 0,
        finalPrice: (planPrice || 0) - (discount || 0),
        rotisPerDay: plan.rotisPerDay || 0,
        deliverySlot: slot || "Morning",
        phone: phone || "",
        area: area || "",
        address: addressDetails || "",
        location: location || "",
        lat: lat || null,
        lng: lng || null,
        // Store clean address for maps
        addressForMaps: addressDetails || "",
        coordinates: lat && lng ? `${lat},${lng}` : null,
        startDate: formattedDate || "",
        status: "active",
        paymentStatus: "pending",
        paymentMethod: "cash_on_delivery",
        createdAt: serverTimestamp(),
        ...(appliedCoupon && {
          couponCode: appliedCoupon.code,
          couponId: appliedCoupon.id,
          couponDiscount: appliedCoupon.discountPercent,
        }),
      };

      await setDoc(doc(db, "subscriptions", user.uid), subData);
      
      // Handle referral coupon creation
      try {
        // Check if user was referred
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists() && userDoc.data().referredBy) {
          const referrerId = userDoc.data().referredBy;
          
          // Create welcome coupon for referred user
          const welcomeCouponId = await createWelcomeCoupon(user.uid, user.email);
          
          // Create referral coupon for referrer
          const referralCouponId = await createReferralCoupon(referrerId, user.uid, user.email);
          
          // Mark referral as completed
          const referralsRef = collection(db, "referrals");
          const referralQuery = query(
            referralsRef,
            where("referrerId", "==", referrerId),
            where("referredUserId", "==", user.uid)
          );
          const referralSnapshot = await getDocs(referralQuery);
          if (!referralSnapshot.empty) {
            const referralId = referralSnapshot.docs[0].id;
            await markReferralCompleted(referralId, referralCouponId, welcomeCouponId);
          }
          
          console.log("🎁 Referral coupons created successfully");
        }
      } catch (error) {
        console.error("❌ Error creating referral coupons:", error);
        // Don't block subscription if referral fails
      }
      
      alert("🎉 Subscription successful! Pay on delivery.");
      onComplete?.();
      navigate("/myplan");
    } catch (err) {
      console.error("❌ Error:", err);
      alert("Something went wrong. Try again.");
    }
  };

  const handlePaymentSuccess = async (paymentData) => {
    try {
      // Check for active subscriptions before creating new one
      const subsRef = collection(db, "subscriptions");
      const existingQ = query(subsRef, where("userId", "==", user.uid), where("status", "==", "active"));
      const snapshot = await getDocs(existingQ);

      // Check if any subscription should be marked as completed
      let hasActiveSubscription = false;
      for (const docSnap of snapshot.docs) {
        const statusResult = await getSubscriptionStatus(docSnap.id);
        if (statusResult.subscription?.status === "active") {
          hasActiveSubscription = true;
          break;
        }
      }

      if (hasActiveSubscription) {
        alert("❌ You already have an active subscription.");
        return;
      }

      const subData = {
        userId: user.uid,
        name: name || "",
        userName: name || "", // For admin compatibility
        userEmail: user.email || "", // For admin compatibility
        email: user.email || "",
        planName: plan.name || "",
        price: planPrice || 0,
        discount: discount || 0,
        finalPrice: (planPrice || 0) - (discount || 0),
        rotisPerDay: plan.rotisPerDay || 0,
        deliverySlot: slot || "Morning",
        phone: phone || "",
        area: area || "",
        address: addressDetails || "",
        location: location || "",
        lat: lat || null,
        lng: lng || null,
        // Store clean address for maps
        addressForMaps: addressDetails || "",
        coordinates: lat && lng ? `${lat},${lng}` : null,
        startDate: formattedDate || "",
        status: "active",
        paymentStatus: "paid",
        paymentId: paymentData.paymentId || "",
        orderId: paymentData.orderId || "",
        createdAt: serverTimestamp(),
        ...(appliedCoupon && {
          couponCode: appliedCoupon.code,
          couponId: appliedCoupon.id,
          couponDiscount: appliedCoupon.discountPercent,
        }),
      };

      // Debug logging for subscription data
      console.log("🔍 Subscription Data Debug (PaymentSuccess) - Area:", subData.area);
      console.log("🔍 Subscription Data Debug (PaymentSuccess) - UserName:", subData.userName);
      console.log("🔍 Subscription Data Debug (PaymentSuccess) - Name:", subData.name);

      await setDoc(doc(db, "subscriptions", user.uid), subData);
      
      // Handle referral coupon creation
      try {
        // Check if user was referred
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists() && userDoc.data().referredBy) {
          const referrerId = userDoc.data().referredBy;
          
          // Create welcome coupon for referred user
          const welcomeCouponId = await createWelcomeCoupon(user.uid, user.email);
          
          // Create referral coupon for referrer
          const referralCouponId = await createReferralCoupon(referrerId, user.uid, user.email);
          
          // Mark referral as completed
          const referralsRef = collection(db, "referrals");
          const referralQuery = query(
            referralsRef,
            where("referrerId", "==", referrerId),
            where("referredUserId", "==", user.uid)
          );
          const referralSnapshot = await getDocs(referralQuery);
          if (!referralSnapshot.empty) {
            const referralId = referralSnapshot.docs[0].id;
            await markReferralCompleted(referralId, referralCouponId, welcomeCouponId);
          }
          
          console.log("🎁 Referral coupons created successfully");
        }
      } catch (error) {
        console.error("❌ Error creating referral coupons:", error);
        // Don't block subscription if referral fails
      }

      if (appliedCoupon) {
        const couponRef = doc(db, 'coupons', appliedCoupon.id);
        const updatedUsedBy = {
            ...appliedCoupon.usedBy,
            [user.uid]: {
              usedAt: serverTimestamp(),
              subscriptionId: user.uid,
            },
        };
        await updateDoc(couponRef, {
          usedBy: updatedUsedBy,
          usedCount: Object.keys(updatedUsedBy).length,
        });
      }

      alert("🎉 Payment successful!");
      onComplete?.();
      navigate("/myplan");
    } catch (err) {
      console.error("❌ Error saving subscription:", err);
      alert("Payment success, but saving failed. Contact support.");
    }
  };

  const handlePaymentError = (err) => {
    console.error("❌ Payment failed:", err);
    alert("Payment failed. Try again.");
  };

  if (loading || checkingSubscription) return <div className="p-4 text-center">Loading...</div>;
  if (!user) return <div className="p-4 text-red-600">❌ Please log in.</div>;
  if (!plan) return <div className="p-4 text-red-600">❌ No plan selected. Redirecting to home...</div>;
  if (hasActiveSubscription) {
    return (
      <div className="p-4 max-w-xl mx-auto">
        <div className="bg-yellow-100 border border-yellow-300 text-yellow-800 rounded-lg p-6 text-center font-semibold mb-6">
          You already have an active subscription.<br />
          Please cancel your current plan before subscribing again.
        </div>
        <button
          className="bg-green-600 text-white px-4 py-2 rounded font-semibold hover:bg-green-700"
          onClick={() => navigate("/myplan")}
        >
          Go to My Plan
        </button>
        {pastSubscriptions.length > 0 && (
          <div className="mt-8">
            <h3 className="text-lg font-bold mb-2 text-green-700">Your Past Subscriptions</h3>
            <ul className="space-y-2">
              {pastSubscriptions.map((sub) => (
                <li key={sub.id} className="bg-white border rounded p-3 flex flex-col md:flex-row md:items-center md:justify-between">
                  <div>
                    <span className="font-semibold text-green-700">{sub.planName}</span> &mdash; {sub.rotisPerDay} rotis/day
                  </div>
                  <div className="text-xs text-gray-500">{sub.status} | {sub.startDate}</div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-4 max-w-xl mx-auto">
      {outOfRange && !giftMode && (
        <div className="p-3 mb-4 bg-red-100 text-red-700 rounded border border-red-300 text-center font-semibold">
          Sorry, we do not deliver to your location yet.<br />
          <button
            className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            onClick={() => setGiftMode(true)}
          >
            Order for someone else in our service area
          </button>
        </div>
      )}
      {outOfRange && giftMode && (
        <div className="p-3 mb-4 bg-blue-50 text-blue-900 rounded border border-blue-300">
          <div className="mb-2 font-semibold">Order for someone in our service area</div>
          <input
            className="w-full border px-2 py-2 mt-2 rounded"
            value={recipientName}
            onChange={e => setRecipientName(e.target.value)}
            placeholder="Recipient Name *"
          />
          <input
            className="w-full border px-2 py-2 mt-2 rounded"
            value={recipientPhone}
            onChange={e => setRecipientPhone(e.target.value)}
            placeholder="Recipient Phone *"
          />
          <select
            value={recipientArea}
            onChange={e => setRecipientArea(e.target.value)}
            className="w-full border px-4 py-2 mt-2 rounded"
            required
          >
            <option value="">Select Area *</option>
            {areaList.map(a => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
          <textarea
            className="w-full border px-2 py-2 mt-2 rounded"
            value={recipientAddress}
            onChange={e => setRecipientAddress(e.target.value)}
            placeholder="Recipient Address *"
          />
          <button
            className="mt-3 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            onClick={async () => {
              // Validate area (simulate lat/lng for area center if needed)
              // For now, assume all areas in areaList are serviceable
              if (!recipientName || !recipientPhone || !recipientArea || !recipientAddress) {
                alert("Please fill all recipient details.");
                return;
              }
              setOutOfRange(false); // Allow proceeding with recipient details
            }}
          >
            Proceed with this recipient
          </button>
          <button
            className="mt-2 ml-2 px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
            onClick={() => setGiftMode(false)}
          >
            Cancel
          </button>
        </div>
      )}
      {/* If in-range or giftMode completed, show normal form but use recipient details if in giftMode */}
      {(!outOfRange || (outOfRange && giftMode && !outOfRange)) && (
        <>
          <h2 className="text-xl font-bold text-green-700 mb-4">
            Subscribe to {plan.name}
          </h2>
          
          {/* Dynamic Roti Selector */}
          {(planData || plan) && (
            <div className="bg-white border rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-gray-800 mb-3">🍽️ Customize Your Plan</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rotis per Day: <span className="text-green-600 font-bold">{rotiCount}</span>
                </label>
                <input
                  type="range"
                  min={Math.max((planData || {}).minRotisPerDay || 2, 2)}
                  max={(planData || {}).maxRotisPerDay || 10}
                  value={rotiCount}
                  onChange={(e) => setRotiCount(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>{Math.max((planData || {}).minRotisPerDay || 2, 2)} rotis</span>
                  <span>{(planData || {}).maxRotisPerDay || 10} rotis</span>
                </div>
              </div>
              
              {/* Price Calculation */}
              <div className="bg-green-50 border border-green-200 rounded p-3">
                <div className="text-sm text-gray-600 mb-2">
                  💰 Price Breakdown:
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>{rotiCount} rotis/day × ₹{(planData || {}).pricePerRoti || 20}</span>
                    <span>₹{rotiCount * ((planData || {}).pricePerRoti || 20)}/day</span>
                  </div>
                  <div className="flex justify-between">
                    <span>× {(planData || {}).deliveryDays || 26} days</span>
                    <span>₹{totalAmount}</span>
                  </div>
                  <div className="border-t pt-1 mt-2">
                    <div className="flex justify-between font-semibold text-green-700">
                      <span>Total Amount:</span>
                      <span>₹{totalAmount}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Delivery Summary */}
              <div className="mt-3 text-sm text-gray-600">
                📦 You'll get <span className="font-semibold">{rotiCount} rotis/day</span> for <span className="font-semibold">{(planData || {}).deliveryDays || 26} days</span>
              </div>
              
              {/* Holiday Note */}
              <div className="mt-2 text-sm text-red-600 font-medium">
                📢 Note: Every Monday will be Holiday
              </div>
            </div>
          )}

          <DeliverySlotSelector value={slot} onChange={setSlot} />
          <input className={`w-full border px-2 py-2 mt-3 rounded ${errors.name ? 'border-red-500' : ''}`}
            value={giftMode ? recipientName : name}
            onChange={e => giftMode ? setRecipientName(e.target.value) : setName(e.target.value)}
            placeholder="Full Name *"
          />
          {errors.name && <div className="text-red-600 text-xs mb-1">* Required</div>}
          <input className={`w-full border px-2 py-2 mt-3 rounded ${errors.phone ? 'border-red-500' : ''}`}
            value={giftMode ? recipientPhone : phone}
            onChange={e => giftMode ? setRecipientPhone(e.target.value) : setPhone(e.target.value)}
            placeholder="Phone *"
          />
          {errors.phone && <div className="text-red-600 text-xs mb-1">* Required</div>}
          <label className="block mb-2 font-medium text-gray-700">Area <span className="text-red-500">*</span></label>
          <select
            value={giftMode ? recipientArea : area}
            onChange={e => giftMode ? setRecipientArea(e.target.value) : setArea(e.target.value)}
            className={`w-full border px-4 py-2 rounded mb-4 ${errors.area ? 'border-red-500' : ''}`}
            required
          >
            <option value="">Select Area</option>
            {areaList.map(a => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
          {errors.area && <div className="text-red-600 text-xs mb-1">* Required</div>}
          <textarea className={`w-full border px-2 py-2 mt-3 rounded ${errors.addressDetails ? 'border-red-500' : ''}`}
            value={giftMode ? recipientAddress : addressDetails}
            onChange={e => giftMode ? setRecipientAddress(e.target.value) : setAddressDetails(e.target.value)}
            placeholder="Address Details *"
          />
          {errors.addressDetails && <div className="text-red-600 text-xs mb-1">* Required</div>}

          <button onClick={handleLocateMe} className="bg-green-700 text-white px-4 py-2 mt-3 rounded">📍 Locate Me</button>
          {errors.location && <div className="text-red-600 text-xs mb-1">* Please use Locate Me</div>}
          {area && <p className="text-sm mt-2 text-green-600">Detected Area: {area}</p>}

          {/* Coupon Code UI */}
          <div className="mt-4">
            <input
              type="text"
              className="w-full border px-3 py-2 rounded"
              value={coupon}
              onChange={(e) => setCoupon(e.target.value)}
              placeholder="Enter Coupon (optional)"
            />
            <button onClick={applyCoupon} className="mt-2 bg-green-600 text-white px-4 py-2 rounded">
              Apply Coupon
            </button>
            {couponMessage && <p className="text-sm mt-1">{couponMessage}</p>}
          </div>

          {/* Confirm & Buttons */}
          <ConfirmSubscription
            planName={plan.name}
            amount={planPrice - discount}
            slot={slot}
            rotisPerDay={rotiCount}
            address={`${location}\n${addressDetails}`}
            startDate={formattedDate}
            name={name}
            phone={phone}
          />

          {showPayment && (
            <div className="mt-6">
              <button
                className="w-full py-3 px-6 rounded-lg font-semibold text-white bg-green-600 hover:bg-green-700 transform hover:scale-105 transition-all shadow-lg"
                onClick={handlePayNow}
              >
                <span className="mr-2"></span>
                Pay ₹{planPrice - discount} with Razorpay
              </button>
              <div className="text-xs text-gray-500 text-center mt-2">
                🔒 Secure payment powered by Razorpay
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SubscribePage;
